import { cn } from "@/lib/cn";
import { useConfirmDialogStore } from "@/lib/confirm-dialog-store";
import { Button } from "./Button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./Dialog";
import { Text } from "./Text";

const CONFIRM_COLOR_CLASS = {
  peligro: "bg-peligro-600 hover:bg-peligro-700",
  advertencia: "bg-advertencia-600 hover:bg-advertencia-700",
} as const;

/**
 * Host único del diálogo de confirmación imperativo (`useConfirmDialog()`,
 * `src/lib/confirm-dialog-store.ts`) · reemplaza `openConfirmModal` de
 * Mantine (9 sitios). Se monta UNA vez en `__root.tsx`, junto al
 * `Toaster` — cualquier feature llama al hook, no le importa dónde vive el
 * diálogo.
 */
export function ConfirmDialogHost() {
  const opened = useConfirmDialogStore((s) => s.opened);
  const options = useConfirmDialogStore((s) => s.options);
  const respond = useConfirmDialogStore((s) => s.respond);
  // Sin `confirmColor`/`danger` el botón queda en el filled default (naranja
  // de marca) · es el caso de una confirmación NO destructiva.
  const colorKey = options?.confirmColor ?? (options?.danger ? "peligro" : undefined);

  return (
    <Dialog open={opened} onOpenChange={(next) => { if (!next) respond(false); }}>
      <DialogContent className="max-w-sm" showClose={false}>
        {options && (
          <>
            <DialogHeader>
              <DialogTitle>{options.title}</DialogTitle>
            </DialogHeader>
            <Text size="sm" c="dimmed">
              {options.message}
            </Text>
            <DialogFooter>
              <Button variant="default" onClick={() => respond(false)}>
                {options.cancelLabel ?? "Cancelar"}
              </Button>
              <Button
                variant="filled"
                leftSection={options.icon}
                className={cn(colorKey && CONFIRM_COLOR_CLASS[colorKey])}
                onClick={() => respond(true)}
              >
                {options.confirmLabel ?? "Confirmar"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export { useConfirmDialog } from "@/lib/confirm-dialog-store";
export type { ConfirmDialogOptions } from "@/lib/confirm-dialog-store";
